"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Admin only",
    rule: "Settings → Users and roles → Roles. Title Roles Management. Add opens Add Role",
  },
  {
    topic: "Admin system role",
    rule: "The row named Admin cannot be edited or deleted. Tooltip: This is a system generated role. Copy still works",
  },
  {
    topic: "Delete",
    rule: "Confirm Are you sure you want to delete \"…\"? Blocked while the role is assigned to users: Please unassign it before deletion",
  },
  {
    topic: "Accountant",
    rule: "Checkbox This Role is for Accountant users. Users on that role get the accountant flag used to approve / cancel payments and bank transfers",
  },
  {
    topic: "Not on plan",
    rule: "A module row can show (Upgrade your plan to get access). Those switches stay off and disabled",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [
      { heading: "1. The list" },
      { heading: "2. Add / Edit / Copy" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mb-4">
    {items.map((text, i) => (
      <li key={i} className="flex items-start gap-3 list-none">
        <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
          {text}
        </p>
      </li>
    ))}
  </ol>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function RolesPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Roles
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Admins — the permission set you assign on Users
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Name a role, then turn switches on per module. That is what a user
            with this role can click in Books. Users only picks the role — it
            does not define the switches.
          </p>
          <Flow
            steps={[
              "Settings",
              "Users and roles",
              "Roles",
              "Add or Edit",
              "switches",
              "Save",
            ]}
          />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. The list
          </h3>
          <Definitions
            items={[
              {
                term: "Columns",
                desc: "Role Name, Description. Actions: Edit, Copy, Delete",
              },
              {
                term: "What it affects",
                desc: "Which named roles exist to pick on Invite User / Update User. It does not change who is signed in until you assign the role on Users",
              },
            ]}
          />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Add / Edit / Copy
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Titles <B>Add Role</B>, <B>Edit Role</B>, or{" "}
            <B>Add Role (Copied)</B>. Copy starts a new role with the same
            switches.
          </p>
          <Steps
            items={[
              <>
                Enter <B>Role Name</B> and <B>Description</B> (both required).
                Optionally tick <B>This Role is for Accountant users</B>.
              </>,
              <>
                In the table, set switches per module. Columns: Module Name,
                Full Access, Create, View, Edit, Delete, Export. Parent rows
                (bold) group children. Not every column shows on every row.
              </>,
              <>
                Click <B>Save</B>. <B>Cancel</B> returns to the list.
              </>,
            ]}
          />
          <Definitions
            items={[
              {
                term: "Typical modules",
                desc: "Full Access turns Create / View / Edit / Delete on together",
              },
              {
                term: "Reports",
                desc: "View and Export only. Child report rows follow the Reports parent switches",
              },
              {
                term: "Analytics",
                desc: "View only. Children follow the Analytics parent View",
              },
              {
                term: "Inventory Config / Other Settings",
                desc: "Settings screens under those groups: View + Edit, or Create / View / Edit / Delete (Additional charges, Incentive)",
              },
              {
                term: "What it affects",
                desc: "Every module gate for users who have this role: which lists they open, and whether they can create, edit, delete, or export",
              },
            ]}
          />
          <Callout type="note">
            The module list is the live company menu, not a fixed Help Center
            catalog. Rows that are off the plan stay disabled.
          </Callout>
          <Screenshot caption="Roles Management list; Add Role with Role Name, Description, Accountant checkbox, permission table, Save" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/settings/users"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Users <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where you assign this role to a person
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/my-profile"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              My Profile <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Shows the signed-in user&apos;s role name
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
